import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contactsService } from '@/services/contacts'
import type { ContactFormData, ContactFilters } from '@/types/contact'

export function useContacts(filters?: ContactFilters) {
  const queryClient = useQueryClient()

  const contactsQuery = useQuery({
    queryKey: ['contacts', filters],
    queryFn: () => contactsService.getAll(filters),
  })

  const createMutation = useMutation({
    mutationFn: (data: ContactFormData) => contactsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ContactFormData> }) =>
      contactsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contactsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const syncToQontoMutation = useMutation({
    mutationFn: (userId: string) => contactsService.syncToQonto(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  const syncAllToQontoMutation = useMutation({
    mutationFn: () => contactsService.syncAllToQonto(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
  })

  return {
    contacts: contactsQuery.data ?? [],
    isLoading: contactsQuery.isLoading,
    error: contactsQuery.error,
    createContact: createMutation.mutateAsync,
    updateContact: updateMutation.mutateAsync,
    deleteContact: deleteMutation.mutateAsync,
    syncToQonto: syncToQontoMutation.mutateAsync,
    syncAllToQonto: syncAllToQontoMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSyncingToQonto: syncToQontoMutation.isPending,
    isSyncingAllToQonto: syncAllToQontoMutation.isPending,
  }
}

export function useContact(id: string) {
  return useQuery({
    queryKey: ['contacts', id],
    queryFn: () => contactsService.getById(id),
    enabled: !!id,
  })
}
